/**
 * SSI / DD Consistency Dialog (SLaM / 3 Borough)
 *
 * Shown when a Lead clinician records an ADHD Diagnostic Decision whose
 * positive/negative direction does not line up with the symptom and
 * day-to-day-impact decisions captured in the Semi-Structured Interview (SSI).
 *
 * WHY THIS EXISTS
 * ---------------
 * In SLaM the SSI is done by one clinician and the Diagnostic Decision by the
 * Lead. If the Lead's outcome disagrees with the SSI's recorded symptom/domain
 * decisions, the Patient Report would otherwise mix messages (e.g. a positive
 * diagnosis alongside an SSI section that reads negative). This dialog lets the
 * Lead reconcile that — by taking over and editing the SSI themselves — without
 * looping the original SSI clinician back in.
 *
 * DESIGN — TWO STEPS, NO ASSUMPTIONS
 * ----------------------------------
 * We deliberately do NOT assert "you are wrong". A borderline case (e.g.
 * "Partially met" where symptoms only sometimes cross the threshold) is a valid
 * clinical judgement, so:
 *   Step 1 (ASK):     Neutral question — "do you want to update the SSI
 *                     decisions to match your outcome?" with a first-class "No".
 *   Step 2 (EXPLAIN): Only if they say Yes. Explains that they'll be given edit
 *                     access to the SSI (a read-only copy of the original is
 *                     kept, the report still names the original clinician, and
 *                     new AI paragraphs generate for review after they change
 *                     something), then asks WHEN — edit now, or finish the
 *                     Diagnostic Decision first.
 *
 * It reuses the shared SLaM completion-dialog chrome (ModuleCompletionDialog +
 * SelectableOptionCard) so it looks and behaves exactly like CIFinishPathDialog
 * and PadRedoDialog — same "select a card, then confirm" pattern, same Back /
 * "Go back and change my decision" affordances. All actions are delegated to the
 * parent via callbacks; this component only owns the wizard step + selections.
 *
 * Copy stays plain-language on purpose — we avoid terms like "override",
 * "mechanical thresholds", "mismatch" or "leadOverride" in the UI. Bare
 * "interview" is also avoided (could be confused with the Clinical Interview);
 * we say "SSI" or "Semi-Structured Interview" instead.
 */

import React, { useEffect, useState } from 'react';
import ModuleCompletionDialog from './ModuleCompletionDialog';
import SelectableOptionCard from './SelectableOptionCard';

// Wizard steps. ASK = "should the SSI change?"; EXPLAIN = consequences +
// "when do you want to make the changes?".
const STEPS = Object.freeze({
  ASK: 'ask',
  EXPLAIN: 'explain',
});

// Shared Tailwind for the action-row buttons. These are plain <button>s (not
// Radix AlertDialog.Action/Cancel) because a multi-step wizard must be able to
// move Back/Continue between steps WITHOUT closing the dialog — closing is only
// ever done explicitly by the parent via the callbacks below. Mirrors the BTN
// object in CIFinishPathDialog so the row looks consistent across the workflow.
const BTN = {
  secondary:
    'py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap',
  primary:
    'flex items-center px-4 py-2 mb-2 font-semibold rounded-md transition-all duration-300 bg-violet-900 text-white hover:bg-violet-800',
  primaryDisabled:
    'flex items-center px-4 py-2 mb-2 font-semibold rounded-md bg-gray-300 text-gray-500 cursor-not-allowed',
};

// "Fully met" and "Partially met" are POSITIVE ADHD outcomes; "Not met" is
// NEGATIVE. Kept in sync with POSITIVE_DECISIONS in utils/ssiDdConsistency.js.
const POSITIVE_DECISIONS = new Set(['Fully met', 'Partially met']);

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string} props.diagnosisDecision - 'Fully met' | 'Partially met' | 'Not met'
 * @param {string} [props.ssiClinicianName='your colleague'] - Original SSI clinician
 * @param {boolean} [props.needsTakeover=false] - True when the Lead must take
 *   over SSI editing before they can change decisions (i.e. they are not already
 *   the effective SSI owner). Controls whether Step 2 shows the takeover
 *   consequences copy.
 * @param {boolean} [props.isProcessing=false] - Loading while the takeover runs
 * @param {() => void} props.onGoBackToDecision - Close and let the Lead re-pick
 *   their Diagnostic Decision (mis-click recovery).
 * @param {() => void} props.onKeepAsIs - Keep the SSI unchanged; records an
 *   acknowledgement and stays on the Diagnostic Decision.
 * @param {() => void} props.onConfirmEditNow - Grant edit access (if needed) and
 *   go to the Semi-Structured Interview now.
 * @param {() => void} props.onConfirmFinishDDFirst - Grant edit access (if
 *   needed) and stay on the Diagnostic Decision to finish it first.
 */
export const SSIDdConsistencyDialog = ({
  open,
  diagnosisDecision = '',
  ssiClinicianName = 'your colleague',
  needsTakeover = false,
  isProcessing = false,
  onGoBackToDecision,
  onKeepAsIs,
  onConfirmEditNow,
  onConfirmFinishDDFirst,
}) => {
  const [step, setStep] = useState(STEPS.ASK);
  // Step 1 selection: 'yes' (update the SSI) | 'no' (keep as is) | null
  const [updateChoice, setUpdateChoice] = useState(null);
  // Step 2 selection: 'now' (edit SSI now) | 'later' (finish DD first) | null
  const [whenChoice, setWhenChoice] = useState(null);

  // Reset the wizard every time it opens so a previous run's choices can't stick.
  useEffect(() => {
    if (open) {
      setStep(STEPS.ASK);
      setUpdateChoice(null);
      setWhenChoice(null);
    }
  }, [open]);

  const isPositive = POSITIVE_DECISIONS.has(diagnosisDecision);
  const outcomeLabel = isPositive ? 'positive' : 'negative';
  const decisionLabel = diagnosisDecision || 'your ADHD decision';

  const titleByStep = {
    [STEPS.ASK]: 'Update the Semi-Structured Interview?',
    [STEPS.EXPLAIN]: needsTakeover
      ? 'Update the SSI yourself'
      : 'When would you like to make your changes?',
  };

  const descriptionByStep = {
    [STEPS.ASK]: '',
    [STEPS.EXPLAIN]: '',
  };

  // Step 1 primary CTA: advance to EXPLAIN when updating, or keep-as-is when not.
  const handleAskContinue = () => {
    if (!updateChoice || isProcessing) return;
    if (updateChoice === 'yes') {
      setStep(STEPS.EXPLAIN);
      return;
    }
    onKeepAsIs?.();
  };

  // Step 2 primary CTA: run the chosen path (both grant edit access when needed).
  const handleExplainContinue = () => {
    if (!whenChoice || isProcessing) return;
    if (whenChoice === 'now') {
      onConfirmEditNow?.();
      return;
    }
    onConfirmFinishDDFirst?.();
  };

  const askCtaLabel =
    updateChoice === 'no' ? 'Keep the SSI decisions' : 'Continue';

  const explainCtaLabel = isProcessing
    ? 'Opening…'
    : whenChoice === 'later'
      ? 'Finish Diagnostic Decision first'
      : 'Go to Semi-Structured Interview';

  const actions =
    step === STEPS.ASK ? (
      <div className="w-full flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className={BTN.secondary}
          disabled={isProcessing}
          onClick={() => onGoBackToDecision?.()}
        >
          Go back and change my decision
        </button>
        <button
          type="button"
          disabled={!updateChoice || isProcessing}
          className={!updateChoice || isProcessing ? BTN.primaryDisabled : BTN.primary}
          onClick={handleAskContinue}
        >
          {askCtaLabel}
        </button>
      </div>
    ) : (
      <div className="w-full flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className={BTN.secondary}
          disabled={isProcessing}
          onClick={() => setStep(STEPS.ASK)}
        >
          Back
        </button>
        <button
          type="button"
          disabled={!whenChoice || isProcessing}
          className={!whenChoice || isProcessing ? BTN.primaryDisabled : BTN.primary}
          onClick={handleExplainContinue}
        >
          {explainCtaLabel}
        </button>
      </div>
    );

  return (
    <ModuleCompletionDialog
      open={open}
      onOpenChange={(next) => {
        // Only react to close requests (Escape). Closing without an explicit
        // choice is treated as "go back" — never triggers a takeover — so an
        // accidental dismiss is always safe. Blocked while a takeover is running.
        if (!next && !isProcessing) {
          onGoBackToDecision?.();
        }
      }}
      title={titleByStep[step]}
      description={descriptionByStep[step]}
      width={640}
      actions={actions}
    >
      {step === STEPS.ASK && (
        <>
          <p className="mb-4 text-sm text-gray-700 text-left">
            You have recorded a <strong>{outcomeLabel}</strong> ADHD outcome
            (<strong>{decisionLabel}</strong>). Based on the symptoms and areas
            of day-to-day impact recorded in the Semi-Structured Interview, the
            SSI currently does not support a <strong>{outcomeLabel}</strong> ADHD
            outcome. Would you like to update those SSI decisions so they
            reflect your outcome?
          </p>
          <div
            className="mb-2 space-y-3 text-left"
            role="radiogroup"
            aria-label="Update the Semi-Structured Interview decisions?"
          >
            <SelectableOptionCard
              selected={updateChoice === 'yes'}
              onSelect={() => setUpdateChoice('yes')}
              disabled={isProcessing}
              title="Yes — update the SSI decisions"
              description={`You will be able to edit the Semi-Structured Interview so it reflects your ${outcomeLabel} outcome.`}
            />
            <SelectableOptionCard
              selected={updateChoice === 'no'}
              onSelect={() => setUpdateChoice('no')}
              disabled={isProcessing}
              title="No — keep the SSI decisions"
              description={`The SSI stays as ${ssiClinicianName} recorded it. Your ${decisionLabel} decision still stands.`}
            />
          </div>
        </>
      )}

      {step === STEPS.EXPLAIN && (
        <>
          {needsTakeover && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
              <p className="font-semibold">Before you continue</p>
              <ul className="mt-2 list-disc list-inside space-y-1.5">
                <li>
                  You will be given access to edit {ssiClinicianName}'s
                  Semi-Structured Interview and its report sections.
                </li>
                <li>
                  A read-only copy of their original answers is kept, and the
                  report still names {ssiClinicianName} as the interviewing
                  clinician.
                </li>
                <li>
                  New AI output paragraphs will be generated once you make
                  changes to the SSI decisions or notes, and will be ready for
                  you to review and approve.
                </li>
              </ul>
            </div>
          )}
          <div
            className="mb-2 space-y-3 text-left"
            role="radiogroup"
            aria-label="When would you like to update the SSI?"
          >
            <SelectableOptionCard
              selected={whenChoice === 'now'}
              onSelect={() => setWhenChoice('now')}
              disabled={isProcessing}
              title="Edit the SSI now"
              description="Update the Semi-Structured Interview, then return to complete the Diagnostic Decision later - your progress will be saved"
            />
            <SelectableOptionCard
              selected={whenChoice === 'later'}
              onSelect={() => setWhenChoice('later')}
              disabled={isProcessing}
              title="Finish the Diagnostic Decision first"
              description="Complete the Diagnostic Decision now and edit the SSI afterwards from the Patient Overview."
            />
          </div>
        </>
      )}
    </ModuleCompletionDialog>
  );
};

export default SSIDdConsistencyDialog;
